/** Shared tier / card display rules for public partners page and internal preview. */

import { toStorageUrl } from './storageUrl';

export const TIER_CONFIG = {
  diamond: { color: '#B9F2FF', glow: 'rgba(185,242,255,0.35)', label: 'Partner that is Diamond', rank: 5 },
  platinum: { color: '#E8E8F0', glow: 'rgba(232,232,240,0.3)', label: 'Platinum Partner', rank: 4 },
  gold: { color: '#FFD700', glow: 'rgba(255,215,0,0.35)', label: 'Gold Partner', rank: 3 },
  silver: { color: '#C0C0C0', glow: 'rgba(192,192,192,0.28)', label: 'Silver Partner', rank: 2 },
  bronze: { color: '#CD7F32', glow: 'rgba(205,127,50,0.28)', label: 'Bronze Partner', rank: 1 },
};

export const TIER_ORDER = ['diamond', 'platinum', 'gold', 'silver', 'bronze'];

export const LOGO_SIZE_OPTIONS = [
  { value: 'large', label: 'Large' },
  { value: 'medium', label: 'Medium' },
  { value: 'small', label: 'Small' },
  { value: 'hidden', label: 'Hidden (no logo)' },
];

export const getSponsorTier = (partner) => partner?.sponsor_tier || partner?.sponsorTier;

export const tierSlug = (partner) => {
  const name = (getSponsorTier(partner)?.tier_name || '').toLowerCase();
  const hit = TIER_ORDER.find((k) => name.includes(k));
  return hit || name.replace(/\s+/g, '') || 'partner';
};

export const getTier = (level) =>
  TIER_CONFIG[level?.toLowerCase?.()] || {
    color: '#ff4444',
    glow: 'rgba(255,68,68,0.25)',
    label: String(level || 'PARTNER').toUpperCase(),
    rank: 0,
  };

export const getTierForPartner = (partner) => getTier(tierSlug(partner));

export const isPremiumPartner = (partner) => ['gold', 'platinum', 'diamond'].includes(tierSlug(partner));

/** Public grid uses premium layout only for diamond/platinum tiers. */
export const usesPremiumGridForTier = (tierKey) => tierKey === 'diamond' || tierKey === 'platinum';

export const getInitials = (name) =>
  name?.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) || 'UK';

export const getTierDisplaySettings = (partnerOrTier) => {
  const tier = partnerOrTier?.company ? getSponsorTier(partnerOrTier) : partnerOrTier;
  const source = tier || {};
  const logoSize = ['large', 'medium', 'small', 'hidden'].includes(String(source.logo_size || '').toLowerCase())
    ? String(source.logo_size).toLowerCase()
    : 'medium';
  const isShowDescription = typeof source.is_show_description === 'boolean' ? source.is_show_description : true;
  const isShowContact = typeof source.is_show_contact === 'boolean' ? source.is_show_contact : true;
  return { logoSize, isShowDescription, isShowContact };
};

export const logoSizePx = (size, mode = 'premium') => {
  const map =
    mode === 'premium'
      ? { large: 76, medium: 60, small: 46, hidden: 0 }
      : { large: 62, medium: 48, small: 38, hidden: 0 };
  return map[size] ?? map.medium;
};

/** Diamond/platinum always show description, contact, and promote hidden logos to large. */
export const applyDiamondPlatinumDisplayOverrides = (baseDisplay, tierKey) => {
  if (tierKey !== 'diamond' && tierKey !== 'platinum') return baseDisplay;
  return {
    ...baseDisplay,
    isShowDescription: true,
    isShowContact: true,
    logoSize: baseDisplay.logoSize === 'hidden' ? 'large' : baseDisplay.logoSize,
  };
};

export const resolveCardDisplay = (partner) => {
  const tierKey = tierSlug(partner);
  const baseDisplay = getTierDisplaySettings(partner);
  return applyDiamondPlatinumDisplayOverrides(baseDisplay, tierKey);
};

export const PUBLIC_API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';
export const PUBLIC_STORAGE_ORIGIN = PUBLIC_API_BASE.replace(/\/api\/?$/i, '');

export function storagePublicUrl(path) {
  return toStorageUrl(path);
}

export function normalizePartnersList(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
}

/** Mock partner for internal live preview (no API). */
export function buildMockPartner(tierDraft = {}) {
  return {
    id: 'preview',
    sponsor_tier: {
      tier_name: tierDraft.tier_name || 'Gold Partner',
      logo_size: tierDraft.logo_size ?? 'medium',
      is_show_description: tierDraft.is_show_description ?? true,
      is_show_contact: tierDraft.is_show_contact ?? true,
    },
    notes: 'Sample partnership notes shown when company description is empty.',
    company: {
      name: 'Preview Robotics Sdn Bhd',
      location: 'Johor Bahru, Malaysia',
      description: 'Sample company description for the public sponsors page preview.',
      gmail: 'hello@preview-robotics.example',
      contact_number: '+60 12-345 6789',
      website_link: 'https://example.com',
    },
  };
}
